// Кнопка и панель «Оформление» — как AppearancePanel в teen-english: режим,
// стиль, палитра. Решение владельца от 28 октября 2026.
//
// До этого здесь стояли три кнопки света прямо в шапке. Выборов стало три, и
// у палитры их по два на режим — в строку шапки это не помещается, поэтому
// одна кнопка и панель поверх, закрывается Esc и щелчком мимо.
//
// Значения токенов — src/appearance.css, выбор — src/lib/theme.ts.

import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DARK_PALETTES, LIGHT_PALETTES, setAppearance, useAppearance } from "../lib/theme";
import { IconCircle, IconMoon, IconPalette, IconSquare, IconSun, IconX } from "./Icons";
import { useDialog } from "./useDialog";

export function ThemeSwitch({ className = "themes__btn", label }: { className?: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={opener}
        type="button"
        className={className}
        aria-label="Оформление"
        title="Оформление"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <IconPalette size={label ? 20 : 17} width={1.8} />
        {label ? <span>{label}</span> : null}
      </button>
      {open ? (
        <AppearancePanel
          onClose={() => {
            setOpen(false);
            opener.current?.focus();
          }}
        />
      ) : null}
    </>
  );
}

function AppearancePanel({ onClose }: { onClose: () => void }) {
  const a = useAppearance();
  const close = useRef<HTMLButtonElement>(null);
  useDialog(close, onClose);

  const palettes = a.mode === "light" ? LIGHT_PALETTES : DARK_PALETTES;
  const current = a.mode === "light" ? a.palLight : a.palDark;
  // В корень страницы, а не на месте кнопки: у боковой колонки и нижней
  // панели мягкого стиля включён backdrop-filter, а он делает элемент рамкой
  // для position: fixed внутри. Окно вставало по краю колонки в 250 px и
  // обрезалось (замечание владельца, 31 октября 2026).
  return createPortal(
    <>
      <div className="look-scrim" onClick={onClose} />
      <aside className="look-panel" role="dialog" aria-modal="true" aria-label="Оформление">
        <header>
          <b>
            <IconPalette size={16} /> Оформление
          </b>
          <button ref={close} type="button" className="look-close" onClick={onClose} aria-label="Закрыть">
            <IconX />
          </button>
        </header>
        <p className="look-label">Режим</p>
        <div className="look-seg" role="group" aria-label="Режим">
          <button type="button" aria-pressed={a.mode === "light"} onClick={() => setAppearance({ mode: "light" })}>
            <IconSun /> <span>Дневной</span>
          </button>
          <button type="button" aria-pressed={a.mode === "dark"} onClick={() => setAppearance({ mode: "dark" })}>
            <IconMoon /> <span>Ночной</span>
          </button>
        </div>
        <p className="look-label">Стиль</p>
        <div className="look-seg" role="group" aria-label="Стиль">
          <button type="button" aria-pressed={a.look === "strict"} onClick={() => setAppearance({ look: "strict" })}>
            <IconSquare /> <span>Строгий</span>
          </button>
          <button type="button" aria-pressed={a.look === "soft"} onClick={() => setAppearance({ look: "soft" })}>
            <IconCircle /> <span>Мягкий</span>
          </button>
        </div>
        <p className="look-label">Палитра · {a.mode === "light" ? "день" : "ночь"}</p>
        <div className="look-swatches" role="group" aria-label="Палитра">
          {palettes.map((p) => (
            <button
              key={p.id}
              type="button"
              className="look-swatch"
              aria-pressed={current === p.id}
              onClick={() =>
                setAppearance(
                  a.mode === "light"
                    ? { palLight: p.id as typeof a.palLight }
                    : { palDark: p.id as typeof a.palDark },
                )
              }
            >
              <span className="look-dots">
                {p.colors.map((c) => (
                  <i key={c} style={{ background: c }} />
                ))}
              </span>
              <span>{p.label}</span>
            </button>
          ))}
        </div>
      </aside>
    </>,
    document.body,
  );
}
